import { useState } from "react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
    Plus,
    Trash2,
    SquarePen,
} from "lucide-react";
import {
    RuleVersion,
} from "../../shared/schema";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useDeletePartner, useGetOffers } from "@/hooks/useLocalStorage";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { OfferForm } from "@/shared/partnerschema";
import { AddOffer } from "./AddOffer";
import RulesPopup from "./RulesPopup";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

interface TableHierarchicalNodeProps {
    topLevelData: any[];
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: number) => void;
    onEditForm: (data: any) => void;
    onViewRules: (data: any) => void;
}

function TableForm({
    topLevelData,
    pageIndex,
    pageSize,
    totalCount,
    onPageChange,
    onEditForm,
    onViewRules,
}: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<OfferForm[]>([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [showPopup, setShowPopup] = React.useState(false);
    const [dataHead, setDataHead] = React.useState<RuleVersion | undefined>();
    const [dataContent, setDataContent] = React.useState<string | null>("");
    const queryClient = useQueryClient();
    const [popupName, setPopupName] = React.useState("");

    const [partnerPopupOpen, setPartnerPopupOpen] = React.useState(false);
    const [selectedPartners, setSelectedPartners] = React.useState<OfferForm>();

    React.useEffect(() => {
        if (topLevelData) {
            setCurrentData(topLevelData);
        }
    }, [topLevelData]);

    const handleViewPartners = (partners: OfferForm) => {
        setSelectedPartners(partners);
        setPartnerPopupOpen(true);
    };

    return (
        <div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Id</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Exchange Partner</TableHead>
                            <TableHead>Available Raw Rules</TableHead>
                            <TableHead>Rule Value Setup</TableHead>
                            <TableHead>Rule With Values</TableHead>
                            <TableHead>Create Default Offer</TableHead>
                            <TableHead>View Default Offer</TableHead>
                            <TableHead>Additional Offer</TableHead>
                            <TableHead>Last Modified</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow key={data.Id}>
                                <TableCell className="font-mono">{data.Id}</TableCell>

                                <TableCell className="font-mono">{data.ExchangeOfferName}</TableCell>

                                <TableCell className="px-4 py-3">
                                    <Button
                                        variant="link"
                                        onClick={() =>
                                            handleViewPartners(data)
                                        }
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                {/* Available Raw Rules — opens RulesPopup */}
                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button
                                        variant="link"
                                        className="max-w-xs truncate"
                                        onClick={() => onViewRules(data)}
                                    >
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button variant="link" className="max-w-xs truncate">
                                        SetUp
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button variant="link" className="max-w-xs truncate">
                                        View
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button variant="link" className="max-w-xs truncate">
                                        Setup Offer
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button variant="link" className="max-w-xs truncate">
                                        View Offer
                                    </Button>
                                </TableCell>

                                <TableCell className="px-4 py-3 max-w-xs truncate">
                                    <Button variant="link" className="max-w-xs truncate">
                                        Setup Additional Offer
                                    </Button>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <div className="flex flex-col">
                                        {data.CreatedDate ? (
                                            <>
                                                <span>
                                                    {new Date(data.CreatedDate).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.CreatedDate).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-xs truncate">
                                    <div className="flex flex-col">
                                        {data.ModifiedDate ? (
                                            <>
                                                <span>
                                                    {new Date(data.ModifiedDate).toLocaleDateString("en-IN", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })}
                                                </span>
                                                <span className="text-xs text-black">
                                                    {new Date(data.ModifiedDate).toLocaleTimeString("en-IN", {
                                                        hour: "2-digit", minute: "2-digit",
                                                    })}
                                                </span>
                                            </>
                                        ) : "-"}
                                    </div>
                                </TableCell>

                                <TableCell>
                                    <div className="flex flex-row items-center space-x-2">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-9 h-9 md:w-10 md:h-10 p-0"
                                                    tooltip="More actions"
                                                >
                                                    <Plus size={20} strokeWidth={2.5} />
                                                    <span className="sr-only">More actions</span>
                                                </Button>
                                            </DropdownMenuTrigger>

                                            <DropdownMenuContent align="end" className="min-w-[180px]">
                                                <DropdownMenuItem
                                                    className="flex items-center px-2 cursor-pointer"
                                                    onClick={() => onEditForm(data)}
                                                >
                                                    <SquarePen size={20} strokeWidth={2.5} className="mr-2" />
                                                    <span className="text-sm">Edit Offer</span>
                                                </DropdownMenuItem>

                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <div className="mt-2">
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>

            <Dialog
                open={partnerPopupOpen}
                onOpenChange={setPartnerPopupOpen}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            Exchange Partners
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">

                        {/* Offer Name */}
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                Offer Name
                            </p>

                            <div className="border rounded-md px-3 py-2 bg-gray-50">
                                {selectedPartners?.ExchangeOfferName}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-semibold mb-1">
                                Exchange Partners
                            </p>

                            <div className="border rounded-md px-3 py-2 bg-gray-50 min-h-[48px]">
                                {selectedPartners?.ExPartnerName?.length ?? 0 > 0 ? (
                                    <div className="flex flex-col gap-1">
                                        {selectedPartners?.ExPartnerName?.map((partner, index) => (
                                            <div
                                                key={index}
                                                className="border rounded px-2 py-1 bg-white"
                                            >
                                                {partner}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    "No Partners Found"
                                )}
                            </div>
                        </div>

                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

            {showPopup && (
                <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                />
            )}

        </div>
    );
}


export function ExchangeOffer() {
    const { setBreadcrumbs } = useBreadcrumbs();
    const [topLevelTypes, setTopLevelTypes] = useState<OfferForm[]>([]);

    // Form dialog states
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editFormData, setEditFormData] = useState<any>(null);

    // Rules popup states
    const [rulesPopupOpen, setRulesPopupOpen] = useState(false);
    const [selectedOfferForRules, setSelectedOfferForRules] = useState<OfferForm | null>(null);

    // Pagination states
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const { data: types, isLoading } = useGetOffers(pageIndex, pageSize);

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: "Partners",
                path: "/",
            },
        ]);
    }, []);

    // ✅ Fix: types is now a dependency so table updates when API data arrives
    React.useEffect(() => {
        setTopLevelTypes(types?.data?.items ?? []);
        setTotalCount(types?.data?.total ?? 0);
    }, [types]);
    console.log("toplevel", topLevelTypes)
    const handleAddForm = () => {
        setEditFormData(null);
        setOpenCreateDialog(true);
    };

    const handleEditForm = (data: any) => {
        setEditFormData(data);
        setOpenCreateDialog(true);
    };

    const handleViewRules = (data: OfferForm) => {
        setSelectedOfferForRules(data);
        setRulesPopupOpen(true);
    };

    return (
        <div className="min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-black">
                            Manage XChange Set up
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            XChange Partners Linked:
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <Button
                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                            size="sm"
                            onClick={handleAddForm}
                        >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                            Add Offer
                        </Button>
                    </div>
                </div>
            </div>

            {!isLoading ? (
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <div className="space-y-2">
                            {topLevelTypes && topLevelTypes.length > 0 ? (
                                <TableForm
                                    topLevelData={topLevelTypes}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                    onEditForm={handleEditForm}
                                    onViewRules={handleViewRules}
                                />
                            ) : (
                                <div className="text-center py-8 text-black">
                                    <p>No Offers found. Create one to get started.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6 text-black">Loading...</div>
            )}

            <AddOffer
                open={openCreateDialog}
                onOpenChange={setOpenCreateDialog}
                initialData={editFormData}
            />

            <RulesPopup
                open={rulesPopupOpen}
                onClose={() => {
                    setRulesPopupOpen(false);
                    setSelectedOfferForRules(null);
                }}
                offerData={selectedOfferForRules ?? undefined}
            />
        </div>
    );
}